/**
 * Admin orders endpoints (read-only view of the `orders` table).
 *   - GET /admin/orders                   — paginated list with filters
 *   - GET /admin/orders/{id}              — full detail (incl. render_snapshot / raw_payload)
 *   - GET /admin/orders/{id}/feeding-pdf  — render the feeding PDF on the fly and download it
 *
 * Re-exported from services/api.ts so call-sites keep the same import surface.
 */

import { apiClient } from '../client';
import type { OrdersResponse, OrderDetail } from '../../types/config';

export const adminListOrders = async (params?: {
  platform?: string;
  plan_kind?: string;
  pdf_status?: string;
  email?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}): Promise<OrdersResponse> => {
  const response = await apiClient.get<OrdersResponse>('/admin/orders', { params });
  return response.data;
};

export const adminGetOrder = async (id: string): Promise<OrderDetail> => {
  const response = await apiClient.get<OrderDetail>(`/admin/orders/${id}`);
  return response.data;
};

/**
 * Render the order's feeding PDF in-process on the backend and trigger a browser
 * download. Bytes are streamed straight back, so this works regardless of where
 * the cron-generated file lives (separate instance / ephemeral volume).
 */
export const downloadOrderFeedingPdf = async (id: string, filename: string): Promise<void> => {
  // Longer timeout than the client default (10s): the backend renders the PDF on
  // the fly (1-5s of WeasyPrint) before it starts streaming.
  const res = await apiClient.get(`/admin/orders/${id}/feeding-pdf`, { responseType: 'blob', timeout: 30000 });
  const url = window.URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
